import React, { useState, useRef, useEffect } from 'react';
import './Programs.css';
import Header from '../components/Header';
import Footer from '../components/Footer';

// --- Data ---

const PROGRAMS = [
  {
    id: 'education',
    icon: '📚',
    title: 'You Can Educate',
    color: '#E3A857',
    description: 'Education is the most powerful way to transform a child\'s life — especially a child who cannot see the world but dreams of shaping it. At Devnar School, we believe that knowledge replaces darkness with possibility.',
    details: [
      'First English-medium school in India for visually challenged children',
      'Classes from Kindergarten to Grade 12',
      'Affiliated with SSC Board, Telangana',
      '100% board exam pass record over the years',
      'Regular academic assessments and personalised learning support',
      'Students prepared for higher studies and careers with confidence'
    ],
    activities: [
      'Elocutions and essay writing',
      'Braille reading and writing',
      'Quiz competitions',
      'Cultural programmes',
      'Inter-school competitions'
    ],
    recognition: [
      'Awarded "Best Institution in India for the Visually Challenged" in 2003 and 2008',
      'Alumni today hold respected positions in diverse fields',
      'Currently supports and nurtures 670 students',
      'Meritorious students honoured with Pratibha Awards'
    ]
  },
  {
    id: 'hostel',
    icon: '🏠',
    title: 'You Can Help',
    color: '#C75C4D',
    description: 'At Devnar, every child is family. Our residential hostel provides free boarding and lodging with safe, separate dormitories for boys and girls. Caring wardens and support staff ensure each child\'s comfort and security.',
    details: [
      'Free boarding and lodging facilities',
      'Separate dormitories for boys and girls',
      'Caring wardens and support staff 24/7',
      'Special assistance for younger students',
      'Nutritious meals — breakfast, lunch, snacks, and dinner',
      'Eco-friendly with solar energy for water heating and lighting'
    ],
    activities: [
      'Music and yoga sessions',
      'Dance classes',
      'Cricket and volleyball',
      'Playground activities',
      'Vedic Mathematics classes',
      'Spoken English classes',
      'Bhagwad Geeta classes'
    ]
  },
  {
    id: 'future',
    icon: '🚀',
    title: 'Expanding Wings',
    color: '#2A3557',
    description: 'Our mission is to reach out to as many visually-challenged children as possible. Our future vision is to increase the strength of the students to 1000+ by 2025.',
    details: [
      'Current infrastructure: 4-floor building in Begumpet, 3-floor building in Ranigunj',
      '30 Classrooms, 4 Computer Labs, Science Labs',
      'Library, Audio Recording Room',
      'Open Auditorium, Seminar Hall (150 capacity)',
      'Administrative sections, hostels, staff-quarters, parking'
    ],
    future_plans: [
      'Ground + 3 Floors building',
      '15 classrooms',
      '10 Staff Quarters and Warden Rooms',
      'Staff Room, Science lab, Resource Room'
    ]
  }
];

const TESTIMONIALS = [
  {
    name: 'Ann Peterson',
    image: 'https://devnarfoundationfortheblind.org/wp-content/uploads/2020/08/testimonials_01-60x60.jpg',
    text: 'Massa eget egestas purus viverra accumsan in. Maecenas pharetra convallis posuere morbi.'
  },
  {
    name: 'Tony Olson',
    image: 'https://devnarfoundationfortheblind.org/wp-content/uploads/2020/09/testimonials_02-2-60x60.jpg',
    text: 'Dictum fusce ut placerat orci nulla. At auctor urna nun id cursus metus convallis aliquam posuere eleifend.'
  },
  {
    name: 'Jane Bryan',
    image: 'https://devnarfoundationfortheblind.org/wp-content/uploads/2020/08/testimonials_03-60x60.jpg',
    text: 'Ultrices vitae auctor eu augue. Risus ultric tristique nulla aliquet enim tortor egestas congue auctor.'
  },
  {
    name: 'Dwayt Harder',
    image: 'https://devnarfoundationfortheblind.org/wp-content/uploads/2020/08/testimonials_04-60x60.jpg',
    text: 'At auctor urna nun id cursus metus convallis aliquam posuere eleifend. Dictum fusce ut placerat orci nulla.'
  }
];

const PARTNERS = [
  'https://devnarfoundationfortheblind.org/wp-content/uploads/2020/08/partners_01.png',
  'https://devnarfoundationfortheblind.org/wp-content/uploads/2020/08/partners_02.png',
  'https://devnarfoundationfortheblind.org/wp-content/uploads/2020/08/partners_03.png',
  'https://devnarfoundationfortheblind.org/wp-content/uploads/2020/08/partners_04.png',
  'https://devnarfoundationfortheblind.org/wp-content/uploads/2020/08/partners_05.png',
  'https://devnarfoundationfortheblind.org/wp-content/uploads/2020/08/partners_06.png'
];

const QUICK_ACTIONS = [
  { icon: '🎓', title: 'You Can Educate', desc: 'Support a child\'s education journey' },
  { icon: '🤝', title: 'You Can Help', desc: 'Contribute to hostel and daily needs' },
  { icon: '🙋', title: 'Become Volunteer', desc: 'Share your time and skills' },
  { icon: '🎁', title: 'Collect Donations', desc: 'Organize fundraising for our cause' }
];

// --- Reusable Components ---

function Reveal({ as: Tag = 'div', className = '', delay = 0, children, ...rest }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref]);
  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? 'reveal-visible' : ''} ${className}`}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

// --- Main Programs Component ---

export default function Programs() {
  const [activeProgram, setActiveProgram] = useState('education');

  const currentProgram = PROGRAMS.find(p => p.id === activeProgram) || PROGRAMS[0];

  return (
    <>
      <Header />
      <div className="programs-page devnar">
        <a className="skip-link" href="#main-programs">Skip to main content</a>

        {/* --- Page Hero --- */}
        <section className="page-hero-programs">
          <div className="hero-background">
            <div className="hero-overlay"></div>
            <div className="hero-pattern"></div>
          </div>
          <div className="wrap hero-content-programs">
            <Reveal>
              <span className="hero-badge">Our Programs</span>
              <h1>Our Programs</h1>
              <p className="hero-subtitle">
                Education, hostel, medical care, sports, computer training, and more — 
                comprehensive support for visually challenged children.
              </p>
            </Reveal>
          </div>
        </section>

        <main id="main-programs" className="wrap page-content-programs">

          {/* --- Quick Actions --- */}
          <section className="quick-actions">
            <div className="actions-grid">
              {QUICK_ACTIONS.map((action, index) => (
                <Reveal key={action.title} className="action-card" delay={index * 60}>
                  <span className="action-icon">{action.icon}</span>
                  <h3>{action.title}</h3>
                  <p>{action.desc}</p>
                </Reveal>
              ))}
            </div>
          </section>

          {/* --- Program Tabs --- */}
          <section className="program-tabs-section">
            <Reveal className="section-title" as="div">
              <span className="eyebrow">Our Programs</span>
              <h2>Comprehensive Support</h2>
              <div className="rule" />
            </Reveal>

            <div className="program-tabs">
              {PROGRAMS.map((program) => (
                <button
                  key={program.id}
                  className={`program-tab ${activeProgram === program.id ? 'active' : ''}`}
                  onClick={() => setActiveProgram(program.id)}
                  style={{ '--tab-color': program.color }}
                >
                  <span className="tab-icon">{program.icon}</span>
                  <span className="tab-title">{program.title}</span>
                </button>
              ))}
            </div>

            <div className="program-content">
              <Reveal className="program-panel" key={currentProgram.id}>
                <div className="program-header" style={{ borderColor: currentProgram.color }}>
                  <span className="program-icon-large" style={{ background: `${currentProgram.color}15`, color: currentProgram.color }}>
                    {currentProgram.icon}
                  </span>
                  <div>
                    <h3>{currentProgram.title}</h3>
                    <p>{currentProgram.description}</p>
                  </div>
                </div>

                <div className="program-details-grid">
                  <div className="program-detail-section">
                    <h4>Key Features</h4>
                    <ul>
                      {currentProgram.details.map((detail, i) => (
                        <li key={i}>{detail}</li>
                      ))}
                    </ul>
                  </div>

                  {currentProgram.activities && (
                    <div className="program-detail-section">
                      <h4>Activities</h4>
                      <ul>
                        {currentProgram.activities.map((activity, i) => (
                          <li key={i}>{activity}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {currentProgram.recognition && (
                    <div className="program-detail-section full-width">
                      <h4>Recognition & Impact</h4>
                      <ul>
                        {currentProgram.recognition.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {currentProgram.future_plans && (
                    <div className="program-detail-section full-width">
                      <h4>Future Plans</h4>
                      <ul>
                        {currentProgram.future_plans.map((plan, i) => (
                          <li key={i}>{plan}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="program-cta">
                  <a href="donation.html" className="btn btn-primary">Support This Program</a>
                  <a href="volunteer-registration.html" className="btn btn-outline">Become a Volunteer</a>
                </div>
              </Reveal>
            </div>
          </section>

          {/* --- Partners --- */}
          <section className="partners-section">
            <Reveal className="section-title" as="div">
              <span className="eyebrow">Our Network</span>
              <h2>Our Partners</h2>
              <div className="rule" />
            </Reveal>
            <div className="partner-grid">
              {PARTNERS.map((logo, index) => (
                <Reveal key={index} className="partner-logo" delay={index * 40}>
                  <img src={logo} alt={`Partner ${index + 1}`} loading="lazy" />
                </Reveal>
              ))}
            </div>
          </section>

          {/* --- Testimonials --- */}
          <section className="testimonials-section">
            <Reveal className="section-title" as="div">
              <span className="eyebrow">Testimonials</span>
              <h2>What People Think About Us</h2>
              <div className="rule" />
            </Reveal>

            <div className="testimonials-grid">
              {TESTIMONIALS.map((testimonial, index) => (
                <Reveal key={testimonial.name} className="testimonial-card" delay={index * 60}>
                  <div className="testimonial-header">
                    <img src={testimonial.image} alt={testimonial.name} loading="lazy" />
                    <h4>{testimonial.name}</h4>
                  </div>
                  <p>"{testimonial.text}"</p>
                </Reveal>
              ))}
            </div>
          </section>

          {/* --- CTA Section --- */}
          <Reveal as="section" className="cta-band-programs">
            <div className="cta-content">
              <span className="cta-eyebrow">Join Our Mission</span>
              <h2>Assistance to Foundations is Help to People</h2>
              <p>
                Your support helps us reach more children, expand our infrastructure, 
                and provide quality education and care to visually challenged students.
              </p>
              <div className="cta-buttons">
                <a className="btn btn-primary" href="donation.html">Donate Now</a>
                <a className="btn btn-outline btn-outline-light" href="volunteer-registration.html">Become a Volunteer</a>
              </div>
            </div>
          </Reveal>

        </main>
      </div>
      <Footer />
    </>
  );
}